import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase, apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { logActivity } from "@/lib/activity";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, ListChecks, CheckCircle2, Circle, Trash2, Pencil, Search, MoreHorizontal, Eye, RotateCcw, X, Download, FileDown } from "lucide-react";
import { StatusBadge, PageHeader, EmptyState, formatDate } from "@/lib/ui";
import { downloadCsv, iso } from "@/lib/csvExport";
import { fetchAndSave } from "@/lib/downloads";

const EMPTY = { title: "", description: "", client_id: "none", assigned_to: "unassigned", due_date: "", priority: "medium", status: "todo", recurrence: "none", recurrence_count: 4 };
const STATUSES = ["todo", "in_progress", "waiting", "completed"];

const TEMPLATES = [
  "Follow up with client", "Schedule appointment", "Request documentation",
  "Review intake", "Follow up with provider", "Complete client documentation",
];

export default function Tasks() {
  const { currentOrgId, user, profile } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filter, setFilter]   = useState(params.get("filter")   || "all");
  const [q, setQ]             = useState(params.get("q")        || "");
  const [clientF, setClientF] = useState(params.get("client")   || "all");
  const [statusF, setStatusF] = useState(params.get("status")   || "all");
  const [prioF, setPrioF]     = useState(params.get("priority") || "all");
  const [assignF, setAssignF] = useState(params.get("assignee") || "all");
  const [dueF, setDueF]       = useState(params.get("due")      || "all");

  // Edit dialog + detail drawer
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY, client_id: params.get("client_id") || "none" });
  const [saving, setSaving] = useState(false);
  const [detailTask, setDetailTask] = useState(null);   // ← currently-open detail drawer

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({
      title: t.title || "", description: t.description || "",
      client_id: t.client_id || "none",
      assigned_to: t.assigned_to || "unassigned",
      due_date: t.due_date || "", priority: t.priority || "medium",
      status: t.status || "todo", recurrence: "none", recurrence_count: 4,
    });
    setOpen(true);
  };
  const openNew = () => { setEditingId(null); setForm({ ...EMPTY, client_id: params.get("client_id") || "none" }); setOpen(true); };
  const openDetail = (t) => setDetailTask(t);
  const closeDetail = () => setDetailTask(null);

  const removeTask = async (t) => {
    if (!window.confirm(`Delete task "${t.title}"? This cannot be undone.`)) return;
    const { error } = await supabase.from("tasks").delete().eq("id", t.id);
    if (error) return toast.error(error.message);
    toast.success("Task deleted");
    logActivity({ organization_id: currentOrgId, actor_user_id: user.id, client_id: t.client_id,
      action: "task.deleted", summary: `deleted task "${t.title}"`, entity_type: "tasks", entity_id: t.id });
    setOpen(false); setDetailTask(null); load();
  };

  const load = async () => {
    if (!currentOrgId) return;
    setLoading(true);
    const [t, c, m] = await Promise.all([
      supabase.from("tasks").select("*,clients(id,first_name,last_name),profiles:assigned_to(id,full_name)").eq("organization_id", currentOrgId).order("created_at", { ascending: false }),
      supabase.from("clients").select("id,first_name,last_name").eq("organization_id", currentOrgId).order("first_name"),
      supabase.from("organization_members").select("user_id,profile:profiles!organization_members_user_id_fkey(id,full_name)").eq("organization_id", currentOrgId).eq("status", "active"),
    ]);
    setRows(t.data || []);
    setClients(c.data || []);
    const list = (m.data || []).map((x) => x.profile).filter(Boolean);
    if (user && !list.some((p) => p.id === user.id)) {
      list.push({ id: user.id, full_name: (typeof window !== "undefined" && window.__clientdesk_full_name) || "You" });
    }
    setMembers(list);
    setLoading(false);
    // If a detail drawer is open, refresh its data from the reload
    if (detailTask) {
      const fresh = (t.data || []).find((r) => r.id === detailTask.id);
      if (fresh) setDetailTask(fresh);
    }
  };

  // Deep-link: open ?id=xxx task detail drawer once rows load
  useEffect(() => {
    const id = params.get("id");
    if (!id || !rows.length || detailTask) return;
    const row = rows.find((r) => r.id === id);
    if (row) { openDetail(row); params.delete("id"); setParams(params, { replace: true }); }
    /* eslint-disable-next-line */
  }, [rows, params.get("id")]);

  useEffect(() => { load(); }, [currentOrgId]);
  useEffect(() => {
    if (profile?.full_name && typeof window !== "undefined") window.__clientdesk_full_name = profile.full_name;
  }, [profile?.full_name]);
  useEffect(() => {
    if (params.get("new") === "1") {
      const preClient = params.get("client_id");
      setForm({ ...EMPTY, client_id: preClient || "none" });
      setEditingId(null); setOpen(true);
      params.delete("new"); params.delete("client_id");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist filters in URL
  useEffect(() => {
    const n = new URLSearchParams(params);
    const setOrDel = (k, v, def) => { if (v && v !== def) n.set(k, v); else n.delete(k); };
    setOrDel("filter",   filter,   "all");
    setOrDel("q",        q,        "");
    setOrDel("client",   clientF,  "all");
    setOrDel("status",   statusF,  "all");
    setOrDel("priority", prioF,    "all");
    setOrDel("assignee", assignF,  "all");
    setOrDel("due",      dueF,     "all");
    setParams(n, { replace: true });
    /* eslint-disable-next-line */
  }, [filter, q, clientF, statusF, prioF, assignF, dueF]);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Task name is required");
    setSaving(true);
    const basePayload = {
      title: form.title.trim(),
      description: form.description || null,
      client_id: form.client_id === "none" ? null : form.client_id || null,
      assigned_to: form.assigned_to === "unassigned" ? null : form.assigned_to || null,
      due_date: form.due_date || null,
      priority: form.priority, status: form.status,
    };
    try {
      if (editingId) {
        const updates = { ...basePayload };
        if (form.status === "completed") updates.completed_at = new Date().toISOString();
        const { data, error } = await supabase.from("tasks").update(updates).eq("id", editingId).select().single();
        if (error) throw new Error(error.message);
        toast.success("Task updated");
        logActivity({ organization_id: currentOrgId, actor_user_id: user.id, client_id: data.client_id,
          action: "task.updated", summary: `updated task "${data.title}"`, entity_type: "tasks", entity_id: data.id });
      } else if (form.recurrence !== "none") {
        if (!form.due_date) { setSaving(false); return toast.error("Due date is required for recurring tasks"); }
        const resp = await apiFetch("/tasks/recurring", {
          method: "POST",
          body: JSON.stringify({ organization_id: currentOrgId, ...basePayload, frequency: form.recurrence, count: Number(form.recurrence_count) || 4 }),
        });
        toast.success(`${resp.count} tasks created`);
      } else {
        const payload = { organization_id: currentOrgId, ...basePayload, created_by: user.id };
        const { data, error } = await supabase.from("tasks").insert(payload).select().single();
        if (error) throw new Error(error.message);
        toast.success("Task created");
        logActivity({ organization_id: currentOrgId, actor_user_id: user.id, client_id: data.client_id,
          action: "task.created", summary: `created task "${data.title}"`, entity_type: "tasks", entity_id: data.id });
      }
      setForm(EMPTY); setEditingId(null); setOpen(false); load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const toggleDone = async (t) => {
    const newStatus = t.status === "completed" ? "todo" : "completed";
    const { error } = await supabase.from("tasks").update({
      status: newStatus,
      completed_at: newStatus === "completed" ? new Date().toISOString() : null,
    }).eq("id", t.id).select();
    if (error) return toast.error(error.message);
    if (newStatus === "completed") {
      logActivity({ organization_id: currentOrgId, actor_user_id: user.id, client_id: t.client_id,
        action: "task.completed", summary: `completed task "${t.title}"`, entity_type: "tasks", entity_id: t.id });
    }
    load();
  };

  // Change task status directly (from the detail drawer)
  const setTaskStatus = async (t, newStatus) => {
    const patch = { status: newStatus };
    if (newStatus === "completed") patch.completed_at = new Date().toISOString();
    else if (t.status === "completed") patch.completed_at = null;
    const { error } = await supabase.from("tasks").update(patch).eq("id", t.id).select();
    if (error) return toast.error(error.message);
    toast.success(`Status changed to ${newStatus.replace(/_/g, " ")}`);
    load();
  };

  const today = new Date(new Date().toDateString());
  const inXDays = (d, n) => d && new Date(d) >= today && new Date(d) < new Date(today.getTime() + n * 86400000);
  const bucketOf = (t) => {
    if (t.status === "completed") return "completed";
    if (t.due_date && new Date(t.due_date) < today) return "overdue";
    if (t.due_date && new Date(t.due_date).toDateString() === today.toDateString()) return "today";
    if (t.status === "in_progress") return "in_progress";
    return "todo";
  };

  // Summary counters — computed from real data
  const counters = useMemo(() => {
    const c = { total: rows.length, todo: 0, in_progress: 0, today: 0, overdue: 0, completed: 0 };
    for (const t of rows) {
      if (t.status === "completed") { c.completed += 1; continue; }
      if (t.status === "in_progress") c.in_progress += 1;
      else c.todo += 1;
      if (t.due_date && new Date(t.due_date).toDateString() === today.toDateString()) c.today += 1;
      if (t.due_date && new Date(t.due_date) < today) c.overdue += 1;
    }
    return c;
    /* eslint-disable-next-line */
  }, [rows]);

  const filtered = useMemo(() => rows.filter((t) => {
    // Primary filter tabs
    if (filter === "mine"      && t.assigned_to !== user.id) return false;
    if (filter === "today"     && !(t.due_date && new Date(t.due_date).toDateString() === today.toDateString() && t.status !== "completed")) return false;
    if (filter === "upcoming"  && !(t.due_date && new Date(t.due_date) > today && t.status !== "completed")) return false;
    if (filter === "overdue"   && !(t.due_date && new Date(t.due_date) < today && t.status !== "completed")) return false;
    if (filter === "completed" && t.status !== "completed") return false;
    // Secondary filters
    if (clientF !== "all" && t.client_id !== clientF) return false;
    if (statusF !== "all" && t.status !== statusF) return false;
    if (prioF   !== "all" && t.priority !== prioF)   return false;
    if (assignF !== "all") {
      if (assignF === "unassigned" && t.assigned_to) return false;
      if (assignF !== "unassigned" && t.assigned_to !== assignF) return false;
    }
    if (dueF === "today"    && !(t.due_date && new Date(t.due_date).toDateString() === today.toDateString())) return false;
    if (dueF === "week"     && !inXDays(t.due_date, 7)) return false;
    if (dueF === "overdue"  && !(t.due_date && new Date(t.due_date) < today && t.status !== "completed")) return false;
    if (dueF === "none"     && t.due_date) return false;
    // Search
    if (q) {
      const s = [t.title, t.description, t.clients?.first_name, t.clients?.last_name, t.profiles?.full_name].filter(Boolean).join(" ").toLowerCase();
      if (!s.includes(q.toLowerCase())) return false;
    }
    return true;
    /* eslint-disable-next-line */
  }), [rows, filter, q, clientF, statusF, prioF, assignF, dueF, user.id]);

  const CounterCard = ({ id, label, value, onClick, active }) => (
    <button data-testid={`tcounter-${id}`} onClick={onClick}
      className={`text-left rounded-2xl border px-4 py-3 min-w-[110px] transition-all hover:shadow-sm bg-white ${active ? "ring-2 ring-[#E8712C]/60 border-[#E8712C]/40" : "border-[#E8E2D9]"}`}>
      <div className="text-xs uppercase tracking-widest text-[#57534E]">{label}</div>
      <div className="text-2xl font-semibold mt-1" data-testid={`tcounter-${id}-value`}>{value}</div>
    </button>
  );

  const RowActions = ({ t }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={`task-actions-${t.id}`} onClick={(e) => e.stopPropagation()} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-[#FAF8F5]"><MoreHorizontal size={16} className="text-[#57534E]" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem data-testid={`taction-view-${t.id}`} onClick={() => openDetail(t)}><Eye size={14} className="mr-2" /> View details</DropdownMenuItem>
        <DropdownMenuItem data-testid={`taction-edit-${t.id}`} onClick={() => openEdit(t)}><Pencil size={14} className="mr-2" /> Edit</DropdownMenuItem>
        {t.status !== "completed" ? (
          <DropdownMenuItem data-testid={`taction-complete-${t.id}`} onClick={() => toggleDone(t)}><CheckCircle2 size={14} className="mr-2" /> Mark complete</DropdownMenuItem>
        ) : (
          <DropdownMenuItem data-testid={`taction-reopen-${t.id}`} onClick={() => toggleDone(t)}><RotateCcw size={14} className="mr-2" /> Reopen</DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid={`taction-delete-${t.id}`} onClick={() => removeTask(t)} className="text-red-600 focus:text-red-600"><Trash2 size={14} className="mr-2" /> Delete</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="tasks-page">
      <PageHeader title="Tasks" subtitle="Track everything your team is working on."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full" data-testid="tasks-export-csv"
              disabled={filtered.length === 0}
              onClick={() => downloadCsv(
                `tasks-${iso(new Date())}`,
                [
                  { key: "title",       label: "Title" },
                  { key: "description", label: "Description" },
                  { key: "status",      label: "Status" },
                  { key: "priority",    label: "Priority" },
                  { key: "due_date",    label: "Due date", get: (t) => iso(t.due_date) },
                  { label: "Client",   get: (t) => t.clients ? `${t.clients.first_name} ${t.clients.last_name}` : "" },
                  { label: "Assignee", get: (t) => t.profiles?.full_name || "" },
                  { label: "Created",  get: (t) => iso(t.created_at) },
                ],
                filtered,
              )}
            ><Download size={14} className="mr-1.5" /> Export CSV</Button>
            <Button data-testid="add-task-btn" onClick={openNew} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"><Plus size={16} className="mr-1.5" /> New task</Button>
          </div>
        } />

      {/* Counters */}
      <div className="flex gap-3 overflow-x-auto pb-1" data-testid="tasks-counters">
        <CounterCard id="total"       label="Total tasks"  value={counters.total}       onClick={() => { setFilter("all"); setStatusF("all"); }}     active={filter === "all" && statusF === "all"} />
        <CounterCard id="todo"        label="To do"        value={counters.todo}        onClick={() => { setFilter("all"); setStatusF("todo"); }}    active={statusF === "todo"} />
        <CounterCard id="in_progress" label="In progress"  value={counters.in_progress} onClick={() => { setFilter("all"); setStatusF("in_progress"); }} active={statusF === "in_progress"} />
        <CounterCard id="today"       label="Due today"    value={counters.today}       onClick={() => { setFilter("today"); setStatusF("all"); }}   active={filter === "today"} />
        <CounterCard id="overdue"     label="Overdue"      value={counters.overdue}     onClick={() => { setFilter("overdue"); setStatusF("all"); }} active={filter === "overdue"} />
        <CounterCard id="completed"   label="Completed"    value={counters.completed}   onClick={() => { setFilter("completed"); setStatusF("all"); }} active={filter === "completed"} />
      </div>

      {/* Primary filter tabs */}
      <div className="flex items-center gap-1 bg-white rounded-full border border-[#E8E2D9] p-1 w-fit flex-wrap">
        {[["all","All"],["mine","My tasks"],["today","Due today"],["upcoming","Upcoming"],["overdue","Overdue"],["completed","Completed"]].map(([k,l]) => (
          <button key={k} data-testid={`tfilter-${k}`} onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === k ? "bg-[#E8712C] text-white" : "text-[#57534E] hover:text-[#1C1917]"}`}>{l}</button>
        ))}
      </div>

      {/* Search + secondary filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
          <Input data-testid="tasks-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tasks…" className="pl-9 bg-white border-[#E8E2D9]" />
        </div>
        <Select value={clientF} onValueChange={setClientF}><SelectTrigger data-testid="tfilter-client" className="w-44 bg-white"><SelectValue placeholder="Client" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All clients</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent></Select>
        <Select value={statusF} onValueChange={setStatusF}><SelectTrigger data-testid="tfilter-status" className="w-40 bg-white"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select>
        <Select value={prioF} onValueChange={setPrioF}><SelectTrigger data-testid="tfilter-priority" className="w-36 bg-white"><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All priorities</SelectItem><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="urgent">Urgent</SelectItem></SelectContent></Select>
        <Select value={assignF} onValueChange={setAssignF}><SelectTrigger data-testid="tfilter-assignee" className="w-44 bg-white"><SelectValue placeholder="Assignee" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All assignees</SelectItem><SelectItem value="unassigned">Unassigned</SelectItem>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name || "—"}</SelectItem>)}</SelectContent></Select>
        <Select value={dueF} onValueChange={setDueF}><SelectTrigger data-testid="tfilter-due" className="w-40 bg-white"><SelectValue placeholder="Due" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Any due date</SelectItem><SelectItem value="today">Today</SelectItem><SelectItem value="week">Next 7 days</SelectItem><SelectItem value="overdue">Overdue</SelectItem><SelectItem value="none">No due date</SelectItem></SelectContent></Select>
      </div>

      {loading ? (
        <div className="text-center py-16 text-[#57534E]">Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={ListChecks} title="No tasks match your filters"
          description="Adjust the search or filters above, or create a new task."
          action={<Button data-testid="empty-add-task" onClick={openNew} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"><Plus size={16} className="mr-1.5" /> New task</Button>} />
      ) : (
        <div className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden">
          {/* Desktop table */}
          <Table className="hidden md:table">
            <TableHeader>
              <TableRow className="border-b border-[#E8E2D9] hover:bg-transparent">
                <TableHead className="text-xs font-semibold uppercase tracking-widest text-[#57534E] w-6"></TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-widest text-[#57534E]">Task</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-widest text-[#57534E]">Client</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-widest text-[#57534E]">Assignee</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-widest text-[#57534E]">Due</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-widest text-[#57534E]">Priority</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-widest text-[#57534E]">Status</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-widest text-[#57534E] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((t) => {
                const overdue = t.due_date && new Date(t.due_date) < today && t.status !== "completed";
                return (
                  <TableRow key={t.id} data-testid={`task-row-${t.id}`} onClick={() => openDetail(t)} className="cursor-pointer">
                    <TableCell className="pr-0">
                      <button data-testid={`toggle-${t.id}`} onClick={(e) => { e.stopPropagation(); toggleDone(t); }} className="text-[#E8712C]">
                        {t.status === "completed" ? <CheckCircle2 size={18} /> : <Circle size={18} />}
                      </button>
                    </TableCell>
                    <TableCell>
                      <div className={`font-medium ${t.status === "completed" ? "line-through text-[#A8A29E]" : ""}`}>{t.title}</div>
                      {t.description && <div className="text-xs text-[#A8A29E] mt-0.5 truncate max-w-md">{t.description}</div>}
                    </TableCell>
                    <TableCell className="text-sm">{t.clients ? <button data-testid={`task-client-link-${t.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/app/clients/${t.client_id}`); }} className="text-[#B85C1F] hover:underline">{t.clients.first_name} {t.clients.last_name}</button> : <span className="text-[#A8A29E]">—</span>}</TableCell>
                    <TableCell className="text-sm text-[#57534E]">{t.profiles?.full_name || <span className="text-[#A8A29E]">Unassigned</span>}</TableCell>
                    <TableCell className={`text-sm ${overdue ? "text-[#B02222] font-medium" : "text-[#57534E]"}`}>{t.due_date ? formatDate(t.due_date) : <span className="text-[#A8A29E]">—</span>}</TableCell>
                    <TableCell><StatusBadge value={t.priority} /></TableCell>
                    <TableCell><StatusBadge value={t.status} /></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`view-task-${t.id}`} onClick={(e) => { e.stopPropagation(); openDetail(t); }} className="text-xs px-2 py-1 rounded-full text-[#57534E] hover:bg-[#FAF8F5]"><Eye size={12} className="inline mr-1" />View</button>
                        <RowActions t={t} />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-[#E8E2D9]">
            {filtered.map((t) => {
              const overdue = t.due_date && new Date(t.due_date) < today && t.status !== "completed";
              return (
                <div key={t.id} className="px-4 py-4 flex items-start gap-3 hover:bg-[#FAF8F5] active:bg-[#FDE9D8]/40 transition-colors">
                  <button data-testid={`toggle-${t.id}`} onClick={() => toggleDone(t)} className="mt-1 text-[#E8712C] flex-shrink-0">
                    {t.status === "completed" ? <CheckCircle2 size={20} /> : <Circle size={20} />}
                  </button>
                  <button data-testid={`task-card-${t.id}`} onClick={() => openDetail(t)} className="flex-1 text-left min-w-0">
                    <div className={`font-medium ${t.status === "completed" ? "line-through text-[#A8A29E]" : ""}`}>{t.title}</div>
                    <div className="text-xs text-[#57534E] mt-1 flex items-center gap-2 flex-wrap">
                      {t.clients && <span>{t.clients.first_name} {t.clients.last_name}</span>}
                      {t.profiles && <span>· {t.profiles.full_name}</span>}
                      {t.due_date && <span className={overdue ? "text-[#B02222] font-medium" : ""}>· {overdue ? "Overdue " : "Due "}{formatDate(t.due_date)}</span>}
                    </div>
                    <div className="flex gap-1.5 mt-1.5"><StatusBadge value={t.priority} /><StatusBadge value={t.status} /></div>
                  </button>
                  <RowActions t={t} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── Task Detail Drawer (side-sheet) ────────────────────────────── */}
      <Sheet open={!!detailTask} onOpenChange={(v) => { if (!v) closeDetail(); }}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-white" data-testid="task-detail-drawer">
          {detailTask && (() => {
            const t = detailTask;
            const overdue = t.due_date && new Date(t.due_date) < today && t.status !== "completed";
            return (
              <>
                <SheetHeader className="pr-8">
                  <SheetTitle className="font-display tracking-tight text-2xl leading-tight" data-testid="task-detail-title">{t.title}</SheetTitle>
                  <SheetDescription asChild><div className="flex gap-2 flex-wrap"><StatusBadge value={t.priority} testid="task-detail-priority" /><StatusBadge value={t.status} testid="task-detail-status" /></div></SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-5">
                  {t.description && (
                    <div>
                      <div className="text-xs uppercase tracking-widest text-[#A8A29E] mb-1.5">Description</div>
                      <div className="text-sm whitespace-pre-wrap" data-testid="task-detail-description">{t.description}</div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4 pt-2 border-t border-[#E8E2D9]">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-[#A8A29E] mb-1">Client</div>
                      <div className="text-sm" data-testid="task-detail-client">
                        {t.clients ? <button onClick={() => { navigate(`/app/clients/${t.client_id}`); closeDetail(); }} className="text-[#B85C1F] hover:underline">{t.clients.first_name} {t.clients.last_name}</button> : <span className="text-[#A8A29E]">—</span>}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-[#A8A29E] mb-1">Assignee</div>
                      <div className="text-sm" data-testid="task-detail-assignee">{t.profiles?.full_name || <span className="text-[#A8A29E]">Unassigned</span>}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-[#A8A29E] mb-1">Due date</div>
                      <div className={`text-sm ${overdue ? "text-[#B02222] font-medium" : ""}`} data-testid="task-detail-due">{t.due_date ? formatDate(t.due_date) : <span className="text-[#A8A29E]">—</span>}{overdue && " · Overdue"}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-[#A8A29E] mb-1">Status</div>
                      <Select value={t.status} onValueChange={(v) => setTaskStatus(t, v)}>
                        <SelectTrigger className="h-8 text-sm" data-testid="task-detail-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-3 border-t border-[#E8E2D9]">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-[#A8A29E] mb-1">Created</div>
                      <div className="text-sm text-[#57534E]" data-testid="task-detail-created">{formatDate(t.created_at)}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-[#A8A29E] mb-1">Last updated</div>
                      <div className="text-sm text-[#57534E]" data-testid="task-detail-updated">{formatDate(t.updated_at)}</div>
                    </div>
                    {t.completed_at && (
                      <div className="col-span-2">
                        <div className="text-xs uppercase tracking-widest text-[#A8A29E] mb-1">Completed</div>
                        <div className="text-sm text-emerald-700" data-testid="task-detail-completed">{formatDate(t.completed_at)}</div>
                      </div>
                    )}
                  </div>

                  <div className="pt-4 border-t border-[#E8E2D9] flex items-center gap-2 flex-wrap">
                    <Button data-testid="task-detail-edit-btn" onClick={() => { closeDetail(); openEdit(t); }} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"><Pencil size={14} className="mr-1.5" /> Edit task</Button>
                    {t.status !== "completed" ? (
                      <Button data-testid="task-detail-complete-btn" onClick={() => toggleDone(t)} variant="outline" className="rounded-full text-emerald-700 border-emerald-200"><CheckCircle2 size={14} className="mr-1.5" /> Mark complete</Button>
                    ) : (
                      <Button data-testid="task-detail-reopen-btn" onClick={() => toggleDone(t)} variant="outline" className="rounded-full"><RotateCcw size={14} className="mr-1.5" /> Reopen</Button>
                    )}
                    <Button
                      data-testid="task-detail-pdf-btn"
                      onClick={async () => {
                        try {
                          const { data: { session } } = await supabase.auth.getSession();
                          if (!session) return toast.error("Please sign in again");
                          await fetchAndSave(
                            `${process.env.REACT_APP_BACKEND_URL}/api/tasks/${t.id}/pdf`,
                            { token: session.access_token,
                              filename: `${(t.title || "task").replace(/\s+/g, "-").toLowerCase()}.pdf` }
                          );
                        } catch (e) { toast.error(e.message || "PDF download failed"); }
                      }}
                      variant="outline" className="rounded-full"
                    ><FileDown size={14} className="mr-1.5" /> Download PDF</Button>
                    <Button
                      data-testid="task-detail-delete-btn"
                      onClick={() => removeTask(t)}
                      variant="outline"
                      className="rounded-full text-red-600 border-red-200"
                    ><Trash2 size={14} className="mr-1.5" /> Delete</Button>
                    <Button data-testid="task-detail-close-btn" onClick={closeDetail} variant="ghost" className="rounded-full ml-auto"><X size={14} className="mr-1" /> Close</Button>
                  </div>
                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>

      {/* ─── Edit dialog (reused for both create and edit) ──────────────── */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditingId(null); }}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{editingId ? "Edit task" : "New task"}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div>
              <div className="flex items-center justify-between mb-1.5"><Label>Task name *</Label>
                <Select value="" onValueChange={(v) => setForm({...form, title: v})}>
                  <SelectTrigger className="w-auto h-7 text-xs border-none text-[#E8712C]" data-testid="template-select"><SelectValue placeholder="Use template" /></SelectTrigger>
                  <SelectContent>{TEMPLATES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Input data-testid="task-title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} />
            </div>
            <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-1.5" data-testid="task-description" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Client</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({...form, client_id: v})}>
                  <SelectTrigger data-testid="task-client" className="mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— None —</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Assigned to</Label>
                <Select value={form.assigned_to} onValueChange={(v) => setForm({...form, assigned_to: v})}>
                  <SelectTrigger data-testid="task-assignee" className="mt-1.5"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent><SelectItem value="unassigned">— Unassigned —</SelectItem>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name || "—"}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({...form, due_date: e.target.value})} className="mt-1.5" data-testid="task-due" /></div>
              <div><Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                  <SelectTrigger data-testid="task-priority" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="urgent">Urgent</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="pt-2 border-t border-[#E8E2D9] grid grid-cols-2 gap-3">
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                  <SelectTrigger className="mt-1.5" data-testid="task-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {!editingId && (
                <div><Label>Repeat</Label>
                  <Select value={form.recurrence} onValueChange={(v) => setForm({...form, recurrence: v})}>
                    <SelectTrigger className="mt-1.5" data-testid="task-recurrence"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Does not repeat</SelectItem><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="biweekly">Every 2 weeks</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent>
                  </Select>
                </div>
              )}
              {!editingId && form.recurrence !== "none" && (
                <div><Label>Occurrences</Label><Input type="number" min="2" max="52" value={form.recurrence_count} onChange={(e) => setForm({...form, recurrence_count: e.target.value})} className="mt-1.5" data-testid="task-recurrence-count" /></div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            {editingId && (
              <Button data-testid="delete-task-btn" variant="outline"
                      onClick={() => { const t = rows.find((r) => r.id === editingId); t && removeTask(t); }}
                      className="rounded-full text-red-600 border-red-200 mr-auto"><Trash2 size={13} className="mr-1" /> Delete</Button>
            )}
            <Button onClick={() => { setOpen(false); setEditingId(null); }} variant="outline" className="rounded-full">Cancel</Button>
            <Button data-testid="save-task-btn" onClick={save} disabled={saving} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">{saving ? "Saving…" : (editingId ? "Save changes" : "Save task")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
